import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostListener,
    OnInit,
    ViewChild,
    ViewContainerRef
} from '@angular/core';
import {DataResult, GroupDescriptor, GroupResult} from '@progress/kendo-data-query';
import {ConfigurationService} from '../configuration.service';
import {NotificationPopupService} from '../../shared/notification-popup/services/notification-popup.service';
import {TenantSettingRow} from '../shared/models/tenant-settings.model';

@Component({
    selector: 'app-system-settings',
    templateUrl: './system-settings.component.html',
    styleUrls: ['./system-settings.component.scss']
})
export class SystemSettingsComponent implements OnInit, AfterViewInit {
    // Floor for very short viewports, and the gutter left below the grid so the card
    // border is not flush with the bottom of the window.
    private static readonly MIN_GRID_HEIGHT = 160;
    private static readonly BOTTOM_GUTTER = 16;

    @ViewChild('gridWrap') private gridWrap: ElementRef;

    public readonly groups: GroupDescriptor[] = [{field: 'group'}];

    public gridMaxHeight = SystemSettingsComponent.MIN_GRID_HEIGHT;

    public gridData: DataResult = {data: [], total: 0};
    public keyCount = 0;
    public searchTerm = '';
    public loaded = false;

    private allRows: TenantSettingRow[] = [];
    private visibleRowCount = 0;

    constructor(private configurationSvc: ConfigurationService,
                private notificationPopupSvc: NotificationPopupService,
                private viewContainerRef: ViewContainerRef,
                private cdr: ChangeDetectorRef) {
    }

    ngOnInit(): void {
        this.configurationSvc.getTenantSettings().subscribe(response => {
            this.allRows = response?.settings ?? [];
            this.keyCount = response?.keyCount ?? 0;
            this.applySearch();
            this.loaded = true;
            this.cdr.detectChanges();
            // #gridWrap is behind *ngIf="loaded" and did not exist yet when
            // ngAfterViewInit ran its first (no-op) measurement - now that
            // detectChanges() has put it in the DOM, measure it for real.
            this.fitGridToViewport();
        }, error => {
            console.error(`Error retrieving tenant settings ${error}`);
            this.loaded = true;
            this.notificationPopupSvc.displayPageNotification(this.viewContainerRef, {
                notificationType: 'error',
                message: error?.status === 403
                    ? 'You do not have access to system configuration.'
                    : 'Failed to load system configuration.'
            });
            this.cdr.detectChanges();
            this.fitGridToViewport();
        });
    }

    ngAfterViewInit(): void {
        this.fitGridToViewport();
    }

    @HostListener('window:resize')
    public onResize(): void {
        this.fitGridToViewport();
    }

    // Measures the wrapper's own position rather than subtracting a hard-coded constant
    // for the chrome above it, so the grid keeps filling the window when the heading,
    // note or search row change height. Material lays inactive tab bodies out in the same
    // row and offsets them horizontally, so top is correct even before this tab is shown.
    private fitGridToViewport(): void {
        const element = this.gridWrap?.nativeElement;
        if (!element) {
            return;
        }
        const available = window.innerHeight
            - element.getBoundingClientRect().top
            - SystemSettingsComponent.BOTTOM_GUTTER;
        this.applyGridHeight(Math.floor(available));

        // The card padding and margins below the grid can still push the document past
        // the viewport, and mat-sidenav-content carries min-height:100vh, so any excess
        // becomes a page scrollbar. Give back exactly what overflows instead of guessing
        // at that stack, so this screen never scrolls at the page level.
        const root = document.documentElement;
        const overflow = root.scrollHeight - root.clientHeight;
        if (overflow > 0) {
            this.applyGridHeight(this.gridMaxHeight - overflow);
        }
    }

    private applyGridHeight(height: number): void {
        const next = Math.max(SystemSettingsComponent.MIN_GRID_HEIGHT, height);
        if (next !== this.gridMaxHeight) {
            this.gridMaxHeight = next;
            this.cdr.detectChanges();
        }
    }

    public get searching(): boolean {
        return this.searchTerm.trim().length > 0;
    }

    public get visibleCount(): number {
        return this.visibleRowCount;
    }

    public handleSearch(value: string): void {
        this.searchTerm = value;
        this.applySearch();
        this.cdr.detectChanges();
    }

    // Values are not searched: nested ones are objects and redacted ones are null.
    private applySearch(): void {
        const term = this.searchTerm.trim().toLowerCase();
        const rows = term.length
            ? this.allRows.filter(row => row.key.toLowerCase().includes(term)
                || row.group.toLowerCase().includes(term))
            : this.allRows;
        this.visibleRowCount = rows.length;
        this.gridData = {data: this.groupInOrder(rows), total: rows.length};
    }

    // Grouped by hand rather than with process(), which only sorts groups asc/desc on the
    // field and would drop OTHER into alphabetical position. The API already returns rows
    // sorted by (group, key) with OTHER last, so one pass over contiguous rows keeps that.
    private groupInOrder(rows: TenantSettingRow[]): GroupResult[] {
        return rows.reduce((groups: GroupResult[], row) => {
            const current = groups[groups.length - 1];
            if (current && current.value === row.group) {
                current.items.push(row);
            } else {
                groups.push({field: 'group', value: row.group, items: [row], aggregates: {}});
            }
            return groups;
        }, []);
    }

    // An arrow property, not a method: the grid calls showIf as a plain function, so a
    // method reference would lose its `this`.
    public isNested = (row: TenantSettingRow): boolean => row.tier === 'nested';

    public isRedacted(row: TenantSettingRow): boolean {
        return row.tier === 'redacted';
    }

    public displayValue(row: TenantSettingRow): string {
        if (row.tier === 'redacted') {
            return 'hidden';
        }
        if (row.tier === 'nested') {
            const unit = row.type === 'array' ? 'item' : 'key';
            return `${row.type} (${row.childCount} ${unit}${row.childCount === 1 ? '' : 's'})`;
        }
        return row.value === null ? 'null' : String(row.value);
    }
}
